import React, { Dispatch, ReactElement, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import CheckboxTree from 'react-checkbox-tree'
import './VoohMediaSelection.scss'
import Input from '../../../components/Form/Input'
import Select from '../../../components/Form/Select'
import {
  VoohMediaCheckboxTreeElement,
  VoohMediaFormat,
  VoohMediaTemplate,
} from '../VoohCampaignForm/types'
import VoohCampaignFormService from '../VoohCampaignForm/service'
import { useSelector } from 'react-redux'
import { RootState, store } from '../../../store'
import { NodeSelectOption } from '../../../components/Form/Select/Select'
import TemplateLabel from '../../../components/MediaSelection/TemplateSelection/TemplateLabel'
import FormRow from '../../../components/Form/FormRow'
import FillingSpinner, { FillingSpinnerType } from '../../../components/FillingSpinner'
import Modal from '../../../components/Modal'
import Button, { ButtonTheme } from '../../../components/Form/Button'
import { voohCampaignFormActions } from '../VoohCampaignForm/slice'

type NodeWithCounts = {
  node: VoohMediaCheckboxTreeElement
  totalLeafs: number
  selectedLeafs: number
}

const stripExistingCount = (label: string): string => label.replace(/\s*\(\d+\/\d+\)$/, '')

const buildNodeWithCounts = (
  node: VoohMediaCheckboxTreeElement,
  checkedSet: Set<string>
): NodeWithCounts => {
  if (!node.children || node.children.length === 0) {
    const isSelected = checkedSet.has(node.value)
    const label = `${stripExistingCount(node.label)}`

    return {
      node: { ...node, label },
      totalLeafs: 1,
      selectedLeafs: isSelected ? 1 : 0,
    }
  }

  const childResults = node.children.map(child => buildNodeWithCounts(child, checkedSet))
  const totalLeafs = childResults.reduce((sum, child) => sum + child.totalLeafs, 0)
  const selectedLeafs = childResults.reduce((sum, child) => sum + child.selectedLeafs, 0)
  const label = `${stripExistingCount(node.label)} (${selectedLeafs}/${totalLeafs})`

  return {
    node: {
      ...node,
      label,
      children: childResults.map(child => child.node),
    },
    totalLeafs,
    selectedLeafs,
  }
}

type Props = {
  setFilterNodesText: Dispatch<SetStateAction<string>>
  setFilterByIds: Dispatch<SetStateAction<string | null>>
  filterByIds: string | null
  filterNodesText: string
  checked: string[]
  setCheckedNodes: Dispatch<SetStateAction<string[]>>
  filteredNodes: VoohMediaCheckboxTreeElement[]
  onCheckMedia?: () => void
  voohMediaTemplateFormat?: VoohMediaFormat
  readonly?: boolean
}

const VoohMediaSelection: React.FC<Props> = ({
  checked,
  setCheckedNodes,
  filterNodesText,
  setFilterNodesText,
  filteredNodes,
  filterByIds,
  setFilterByIds,
  onCheckMedia,
  voohMediaTemplateFormat,
  readonly,
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(['all'])
  const mediaTemplates = useSelector(
    (state: RootState) => state.voohCampaignForm.form.mediaTemplates
  )
  const loadingTemplates = useSelector(
    (state: RootState) => state.voohCampaignForm.loadingTemplates
  )
  const [removingTemplate, setRemovingTemplate] = useState<VoohMediaTemplate | null>(null)
  const [selectedVoohMediaTemplate, setSelectedVoohMediaTemplate] = useState<string | null>(null)
  const nodesWithCounts = useMemo(() => {
    if (!filteredNodes || !filteredNodes.length) {
      return []
    }
    const checkedSet = new Set(checked)
    return filteredNodes.map(node => buildNodeWithCounts(node, checkedSet).node)
  }, [filteredNodes, checked])

  const options: NodeSelectOption[] | undefined = mediaTemplates.map(template => ({
    value: template.id,
    label: (
      <TemplateLabel
        onClick={() => setRemovingTemplate(template)}
        template={template}
      />
    ),
  }))

  useEffect(() => {
    if (voohMediaTemplateFormat) {
      VoohCampaignFormService.getVoohMediaTemplates(voohMediaTemplateFormat)
    }
  }, [])

  const onChangeTemplate = (templateId: string): void => {
    setSelectedVoohMediaTemplate(templateId)
    const mediaTemplate = mediaTemplates.find(template => template.id === templateId)
    if (mediaTemplate) {
      const checkTemplateNodes: string[] = []
      JSON.stringify(filteredNodes[0].children, (_, nestedValue) => {
        if (
          nestedValue &&
          !nestedValue?.children &&
          nestedValue.value &&
          mediaTemplate.media.some(el => el.id.toString() === nestedValue.value.split('_')[0])
        ) {
          checkTemplateNodes.push(nestedValue.value)
        }
        return nestedValue
      })
      setCheckedNodes(checkTemplateNodes)
    }
  }

  const removeMediaTemplateFooter: ReactElement = (
    <div className='Modal__footer'>
      <Button
        onClick={() => {
          if (removingTemplate && voohMediaTemplateFormat) {
            store.dispatch(voohCampaignFormActions.setLaodingVoohMediaTemplates(true))
            VoohCampaignFormService.removeVoohMediaTemplate(removingTemplate.id).then(res => {
              if (res.data) {
                VoohCampaignFormService.getVoohMediaTemplates(voohMediaTemplateFormat)
              }
              setRemovingTemplate(null)
            })
          }
        }}
      >
        {t('common.remove')}
      </Button>
      <Button
        theme={ButtonTheme.BLUE_OUTLINE}
        onClick={() => {
          setRemovingTemplate(null)
        }}
      >
        {t('common.cancel')}
      </Button>
    </div>
  )

  const Body: ReactElement = (
    <Trans
      i18nKey='mediaSelection.template.removeMessage'
      values={{
        template:
          mediaTemplates.find(template => template.id === selectedVoohMediaTemplate)?.name || '',
      }}
      components={{ template: <b /> }}
    />
  )

  return (
    <div className='MediaSelection'>
      <Input
        title={t('common.search')}
        className={'MediaSelectionModal__search-input'}
        id='text'
        placeholder={t('common.search')}
        value={filterNodesText}
        onChange={(val: string) => {
          setFilterNodesText(val)
        }}
      />

      <Input
        disabled={readonly}
        title={t('form.campaign.details.voohMediaNodes.clubsAsId')}
        className={'MediaSelectionModal__search-input'}
        id='text'
        placeholder={t('form.campaign.details.voohMediaNodes.selectMediaByClubsAsId')}
        value={filterByIds || ''}
        onChange={(val: string) => {
          setFilterByIds(val)
        }}
      />

      <FormRow className={'FormRow--vooh-template-selection'}>
        <Select
          id='template-media'
          className='MediaSelection__templates'
          title={t('mediaSelection.template.title')}
          onChange={onChangeTemplate}
          options={options || []}
          disabled={loadingTemplates || readonly}
          value={selectedVoohMediaTemplate}
        />
        {removingTemplate && (
          <Modal
            onClose={() => {
              setRemovingTemplate(null)
            }}
            footer={removeMediaTemplateFooter}
            isOpen
          >
            {Body}
          </Modal>
        )}
        {loadingTemplates && <FillingSpinner type={FillingSpinnerType.STANDARD} />}
      </FormRow>

      <CheckboxTree
        nameAsArray={true}
        showNodeIcon={true}
        showNodeTitles={true}
        nodes={nodesWithCounts}
        checked={checked}
        expanded={expanded}
        showExpandAll={true}
        onCheck={(checked: string[]) => {
          setCheckedNodes(checked)
          setSelectedVoohMediaTemplate(null)
          onCheckMedia && onCheckMedia()
        }}
        onExpand={(expanded: string[]) => {
          setExpanded(expanded)
        }}
      />
    </div>
  )
}

export default VoohMediaSelection
